import { useState, useEffect } from 'react';
import axios from 'axios';

const useOnboarding = () => {
  const [onboardingData, setOnboardingData] = useState({
    domains: false,
    emails: false,
    contacts: false,
    templates: false,
    campaigns: false,
    isNewUser: false,
    loading: true
  });

  const [showWelcome, setShowWelcome] = useState(false);

  const checkOnboardingStatus = async () => {
    try {
      setOnboardingData(prev => ({ ...prev, loading: true }));

      // Check all the required data in parallel
      const [
        domainsResponse,
        emailsResponse,
        contactsResponse,
        templatesResponse,
        campaignsResponse
      ] = await Promise.all([
        axios.get('/api/domains'),
        axios.get('/api/emails/verified'),
        axios.get('/api/contacts/groups'),
        axios.get('/api/templates'),
        axios.get('/api/campaigns')
      ]);

      // Check if user has domains
      const hasDomains = Array.isArray(domainsResponse.data) && domainsResponse.data.length > 0;

      // Check if user has verified emails
      let hasVerifiedEmails = false;
      if (Array.isArray(emailsResponse.data)) {
        hasVerifiedEmails = emailsResponse.data.some(email => email.isVerified);
      } else if (emailsResponse.data && typeof emailsResponse.data === 'object') {
        const emailsArray = emailsResponse.data.emails || emailsResponse.data.data || [];
        hasVerifiedEmails = Array.isArray(emailsArray) && emailsArray.some(email => email.isVerified);
      }

      // Check if user has contact groups
      const hasContacts = Array.isArray(contactsResponse.data) && contactsResponse.data.length > 0;

      // Check if user has templates
      const hasTemplates = Array.isArray(templatesResponse.data) && templatesResponse.data.length > 0;

      // Check if user has campaigns
      const hasCampaigns = Array.isArray(campaignsResponse.data) && campaignsResponse.data.length > 0;

      // Determine if user is new (no data in any section)
      const isNewUser = !hasDomains && !hasVerifiedEmails && !hasContacts && !hasTemplates && !hasCampaigns;

      const newOnboardingData = {
        domains: hasDomains,
        emails: hasVerifiedEmails,
        contacts: hasContacts,
        templates: hasTemplates,
        campaigns: hasCampaigns,
        isNewUser: isNewUser,
        loading: false
      };

      setOnboardingData(newOnboardingData);

      // Show welcome dialog for new users (only once per session)
      if (isNewUser && !sessionStorage.getItem('welcomeShown')) {
        setShowWelcome(true);
        sessionStorage.setItem('welcomeShown', 'true');
      }

      return newOnboardingData;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setOnboardingData(prev => ({ ...prev, loading: false }));
      return null;
    }
  };

  const refreshOnboardingData = () => {
    return checkOnboardingStatus();
  };

  const handleWelcomeClose = () => {
    setShowWelcome(false);
  };

  const handleWelcomeStart = () => {
    setShowWelcome(false);
    // Additional logic can be added here if needed
  };

  // Check if onboarding is complete
  const isOnboardingComplete = () => {
    return onboardingData.domains && 
           onboardingData.emails && 
           onboardingData.contacts && 
           onboardingData.templates && 
           onboardingData.campaigns;
  };

  // Get completion percentage
  const getCompletionPercentage = () => {
    const steps = ['domains', 'emails', 'contacts', 'templates', 'campaigns'];
    const completedSteps = steps.filter(step => onboardingData[step]).length;
    return Math.round((completedSteps / steps.length) * 100);
  };

  // Get next step suggestion
  const getNextStep = () => {
    const steps = [
      { key: 'domains', title: 'Add Domains', path: '/domains' },
      { key: 'emails', title: 'Add Working Emails', path: '/emails' },
      { key: 'contacts', title: 'Add Contacts', path: '/contacts' },
      { key: 'templates', title: 'Add Templates', path: '/templates' },
      { key: 'campaigns', title: 'Create Campaign', path: '/campaigns' }
    ];

    return steps.find(step => !onboardingData[step.key]) || null;
  };

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  return {
    onboardingData,
    showWelcome,
    handleWelcomeClose,
    handleWelcomeStart,
    refreshOnboardingData,
    isOnboardingComplete: isOnboardingComplete(),
    completionPercentage: getCompletionPercentage(),
    nextStep: getNextStep(),
    loading: onboardingData.loading
  };
};

export default useOnboarding; 